import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import LoadingSpinner from 'component/LoadingSpinner';
import ClusterCombobox from 'component/ClusterCombobox';
import NexusDistributorService from 'service/NexusDistributorService';
import { NexusDistributorCreateReq } from 'domain/payload/request/nexusDistributor/NexusDistributorCreateReq';
import { NexusDistributorUpdateReq } from 'domain/payload/request/nexusDistributor/NexusDistributorUpdateReq';
import { NexusDistributorWithdrawalChannelConfigResp } from 'domain/payload/response/nexusDistributor/NexusDistributorWithdrawalChannelConfigResp';

const NexusDistributorForm: React.FC = () => {
    const { id } = useParams();
    const { profile } = useSelector((state: any) => state.auth);
    const token = profile?.data?.token;
    const service = useMemo(() => NexusDistributorService(), []);

    const navigate = useNavigate();

    // form state
    const [distributorName, setDistributorName] = useState('');
    const [distributorCode, setDistributorCode] = useState('');
    const [picName, setPicName] = useState('');
    const [picEmail, setPicEmail] = useState('');
    const [initialPicEmail, setInitialPicEmail] = useState('');
    const [picPhone, setPicPhone] = useState('');
    const [bankName, setBankName] = useState('');
    const [bankAccount, setBankAccount] = useState('');

    // config state
    const [bankChannels, setBankChannels] = useState<NexusDistributorWithdrawalChannelConfigResp[]>([]);

    // loading state
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);
    const [isLoadingConfig, setIsLoadingConfig] = useState(false);
    const [isLoadingSave, setIsLoadingSave] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setIsLoadingSave(true);
        try {
            if (id === "0") {
                await nexusDistributorCreate();
                alert(`Nexus Distributor ${distributorName} successfully created!`);
                navigate("/nexus-distributor");
            } else {
                await nexusDistributorUpdate();
                if (initialPicEmail && initialPicEmail.trim().toLowerCase() !== picEmail.trim().toLowerCase()) {
                    alert(`Distributor ${distributorName} berhasil diperbarui. Kami akan mengirim ulang email onboarding ke ${picEmail}.`);
                } else {
                    alert(`Distributor ${distributorName} berhasil diperbarui.`);
                }
                navigate("/nexus-distributor");
            }
            setIsLoadingSave(false);

        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error || 'An error occurred while saving Nexus Distributor';
            alert(`Error: ${errorMessage}`);
            setIsLoadingSave(false);
        }

    };

    useEffect(() => {
        const loadInitialData = async () => {
            if (!token) {
                window.alert('Token tidak ditemukan. Silakan login kembali.');
                return;
            }

            setIsLoadingConfig(true);
            setIsLoadingDetail(id !== "0");

            try {
                const configRequest = service.nexusDistributorWithdrawalChannelConfig(token);
                const detailRequest = id !== "0" ? service.nexusDistributorDetail(token, id!) : null;

                const [configResponse, detailResponse] = await Promise.all([
                    configRequest,
                    detailRequest ? detailRequest : Promise.resolve(null),
                ]);

                if (configResponse?.data) {
                    setBankChannels(configResponse.data);
                }

                if (detailResponse) {
                    setDistributorName(detailResponse?.data?.distributorName || "");
                    setDistributorCode(detailResponse?.data?.distributorCode || "");
                    setPicName(detailResponse?.data?.picName || "");
                    setPicEmail(detailResponse?.data?.picEmail || "");
                    setInitialPicEmail(detailResponse?.data?.picEmail || "");
                    setPicPhone(detailResponse?.data?.picPhone || "");
                    setBankName(detailResponse?.data?.bankName || "");
                    setBankAccount(detailResponse?.data?.bankAccount || "");
                }

            } catch (error: any) {
                const errorMessage = error.response?.data?.message || error.message || 'An error occurred while fetching Nexus Distributpr data';
                alert(`Error: ${errorMessage}`);
            } finally {
                setIsLoadingConfig(false);
                setIsLoadingDetail(false);
            }
        };

        loadInitialData();
    }, [id, token, service]);

    const nexusDistributorCreate = async () => {
        if (!token) {
            window.alert('Token tidak ditemukan. Silakan login kembali.');
            return;
        }

        try {
            var request: NexusDistributorCreateReq = {
                distributorName: distributorName,
                distributorCode: distributorCode,
                picName: picName,
                picEmail: picEmail,
                picPhone: picPhone,
                bankName: bankName,
                bankAccount: bankAccount,
            };

            await service.nexusDistributorCreate(token, request);
        } catch (error: any) {
            console.error(error);
            throw error;
        }
    };

    const nexusDistributorUpdate = async () => {
        if (!token) {
            window.alert('Token tidak ditemukan. Silakan login kembali.');
            return;
        }

        try {
            var request: NexusDistributorUpdateReq = {
                id: id!,
                distributorName: distributorName,
                distributorCode: distributorCode,
                picName: picName,
                picEmail: picEmail,
                picPhone: picPhone,
                bankName: bankName,
                bankAccount: bankAccount,
            };

            await service.nexusDistributorUpdate(token, request);
        } catch (error: any) {
            console.error(error);
            throw error;
        }
    };

    const bankOptions = useMemo(
        () =>
            bankChannels.map((ch) => ({
                value: ch.channelCode,
                label: ch.channelName,
                logoUrl: ch.logoUrl,
            })),
        [bankChannels]
    );

    const handleBankAccountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const numericValue = e.target.value.replace(/\D/g, '');
        setBankAccount(numericValue);
    };

    return (
        <div className="page-wrapper">
            <div className="row">
                <div className="col-12">
                    <h4 className='mb-4'>{id === "0" ? "Create Nexus Distributor" : "Update Nexus Distributor"}</h4>
                </div>

                <div className="col-12">
                    <div className="p-4" style={{ background: 'white' }}>
                        {isLoadingDetail ?
                            <LoadingSpinner /> :
                            <form onSubmit={handleSubmit}>
                                <div className="row g-3">
                                    <h5 className="col-12 mb-4">Nexus Distributor Information</h5>
                                    <div className="col-md-6">
                                        <label htmlFor="name" className="form-label fw-semibold">Distributor Code</label>
                                        <input
                                            type="text"
                                            id="name"
                                            className="form-control form-control-compact"
                                            value={distributorCode}
                                            placeholder="Code"
                                            onChange={(e) => setDistributorCode(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="name" className="form-label fw-semibold">Distributor Name</label>
                                        <input
                                            type="text"
                                            id="name"
                                            className="form-control form-control-compact"
                                            value={distributorName}
                                            placeholder="Name"
                                            onChange={(e) => setDistributorName(e.target.value)}
                                            required
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label htmlFor="picName" className="form-label fw-semibold">PIC Name</label>
                                        <input
                                            type="text"
                                            id="picName"
                                            className="form-control form-control-compact"
                                            value={picName}
                                            placeholder="PIC Name"
                                            onChange={(e) => setPicName(e.target.value)}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label htmlFor="picEmail" className="form-label fw-semibold">PIC Email</label>
                                        <input
                                            type="text"
                                            id="picEmail"
                                            className="form-control form-control-compact"
                                            value={picEmail}
                                            placeholder="PIC Email"
                                            onChange={(e) => setPicEmail(e.target.value)}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label htmlFor="picPhone" className="form-label fw-semibold">PIC Phone</label>
                                        <input
                                            type="text"
                                            id="picPhone"
                                            className="form-control form-control-compact"
                                            value={picPhone}
                                            placeholder="PIC Phone"
                                            onChange={(e) => setPicPhone(e.target.value)}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Bank Name</label>
                                        <ClusterCombobox
                                            value={bankName}
                                            onChange={setBankName}
                                            options={bankOptions}
                                            isLoading={isLoadingConfig}
                                            placeholder="Select or search bank..."
                                            allowFreeText={false}
                                            noDataText="Tidak ada data bank"
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label htmlFor="bankAccount" className="form-label fw-semibold">Bank Account</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            id="bankAccount"
                                            className="form-control form-control-compact"
                                            value={bankAccount}
                                            placeholder="Bank Account Number"
                                            onChange={handleBankAccountChange}
                                        />
                                    </div>
                                </div>

                                <div className="w-100 d-flex justify-content-end mt-4">
                                    <Link to="/nexus-distributor">
                                        <button className="btn btn-outline-primary">Cancel</button>
                                    </Link>
                                    <button
                                        type="submit"
                                        className="btn btn-primary d-flex justify-content-center align-items-center ms-2"
                                        disabled={isLoadingSave}
                                    >
                                        {isLoadingSave && (
                                            <>
                                                <LoadingSpinner reverse={true} />
                                                <p className="ms-2 mb-0"></p>
                                            </>
                                        )}
                                        Save
                                    </button>
                                </div>
                            </form>
                        }
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NexusDistributorForm;